import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { LoadingSpinner } from "../LoadingSpinner";
import { API_BASE_URL, LoadingSpinnerMeta } from "../../data";
import {
    LocationIcon,
    ArrowsExpandIcon,
    ArrowsContractIcon,
    LogoIcon
} from "../../icons";

import { ShiftStats } from "../../data/types";
import { useAuth } from "../../routes/auth";

// Area-level aggregated stats
type AreaStats = {
    area_id: number;
    area_name: string;
    accepted_count: number;
    checked_in_count: number;
    checkin_percentage: number;
    shift_count: number;
};

type CountryCheckInStats = {
    accepted_count: number;
    checked_in_count: number;
    unique_accepted_count: number;
    unique_checked_in_count: number;
};

type CountryTotals = {
    eu: CountryCheckInStats;
    uk_ireland: CountryCheckInStats;
};

type Props = {
    isActive: boolean
    areaIds?: number[]
}

const REFRESH_INTERVAL_MS = 10000;
const AUTO_SCROLL_SPEED = 1; // pixels per frame
const AUTO_SCROLL_PAUSE_MS = 3000; // pause at top/bottom

const getCheckinBarColor = (percentage: number) => {
    if (percentage >= 70) return 'bg-green-500';
    if (percentage >= 30) return 'bg-amber-500';
    return 'bg-red-500';
};

const getCheckinTextColor = (percentage: number) => {
    if (percentage >= 70) return 'text-green-600 dark:text-green-400';
    if (percentage >= 30) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
};

const getCheckinBorderColor = (percentage: number) => {
    if (percentage >= 70) return 'border-s-green-500';
    if (percentage >= 30) return 'border-s-amber-500';
    return 'border-s-red-500';
};

export const CheckinShiftOperations = (props: Props) => {
    const { isActive, areaIds } = props;
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';

    const [shiftData, setShiftData] = useState<ShiftStats[]>([]);
    const [uniqueTotals, setUniqueTotals] = useState<{ unique_accepted_count: number; unique_checked_in_count: number; unique_checkin_percentage: number } | null>(null);
    const [areaUniqueTotals, setAreaUniqueTotals] = useState<Record<number, { unique_accepted_count: number; unique_checked_in_count: number; unique_checkin_percentage: number }>>({});
    const [countryTotals, setCountryTotals] = useState<CountryTotals | null>(null);
    const [countUniqueVolunteers, setCountUniqueVolunteers] = useState(true);
    const [showCountrySplit, setShowCountrySplit] = useState(false);
    const [filterByTime, setFilterByTime] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [isFullScreen, setIsFullScreen] = useState(false);
    const [showCursor, setShowCursor] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const cursorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const autoScrollRef = useRef<{ direction: 'down' | 'up'; paused: boolean; pauseTimeout: ReturnType<typeof setTimeout> | null }>({
        direction: 'down',
        paused: false,
        pauseTimeout: null
    });

    // Aggregate shift data into area-level stats
    const areaStats = useMemo((): AreaStats[] => {
        const groups: { [key: number]: AreaStats } = {};

        shiftData.forEach(shift => {
            if (!groups[shift.area_id]) {
                groups[shift.area_id] = {
                    area_id: shift.area_id,
                    area_name: shift.area.name,
                    accepted_count: 0,
                    checked_in_count: 0,
                    checkin_percentage: 0,
                    shift_count: 0
                };
            }
            groups[shift.area_id].accepted_count += shift.accepted_count;
            groups[shift.area_id].checked_in_count += shift.checked_in_count;
            groups[shift.area_id].shift_count += 1;
        });

        // Calculate percentages and filter out areas with no allocations
        return Object.values(groups)
            .filter(area => area.accepted_count > 0)
            .map(area => ({
                ...area,
                checkin_percentage: Math.round((area.checked_in_count / area.accepted_count) * 100) // Check against allocated
            }))
            .sort((a, b) => a.area_name.localeCompare(b.area_name));

    }, [shiftData]);

    // Calculate totals across all areas
    const totalStats = useMemo(() => {
        const totals = areaStats.reduce( // Factor in total allocations
            (acc, area) => ({
                accepted_count: acc.accepted_count + area.accepted_count,
                checked_in_count: acc.checked_in_count + area.checked_in_count,
                shift_count: acc.shift_count + area.shift_count,
                area_count: acc.area_count + 1
            }),
            { accepted_count: 0, checked_in_count: 0, shift_count: 0, area_count: 0 }
        );

        return {
            ...totals,
            checkin_percentage: totals.accepted_count > 0
                ? Math.round((totals.checked_in_count / totals.accepted_count) * 100)
                : 0
        };
    }, [areaStats]);

    const fetchShiftStats = useCallback(async () => {

        if (!areaIds || areaIds.length === 0) {
            setShiftData([]);
            setUniqueTotals(null);
            setAreaUniqueTotals({});
            setCountryTotals(null);
            return;
        }

        if (shiftData.length === 0) setLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams();
            areaIds.forEach(id => queryParams.append('area_ids[]', id.toString()));
            queryParams.append('filter_by_time', filterByTime ? '1' : '0');

            const response = await fetch(`${API_BASE_URL}/check-in/stats/?${queryParams.toString()}`, {
                credentials: 'include'
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to load shift statistics');
            }

            const data = await response.json();
            setShiftData(data.shifts);
            setUniqueTotals(data.unique_totals);
            setAreaUniqueTotals(data.area_unique_totals ?? {});
            setCountryTotals(data.country_totals ?? null);

        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    }, [areaIds, shiftData.length, filterByTime]);

    // Initial Fetch & Periodic Refresh
    useEffect(() => {
        if (isActive && areaIds && areaIds.length > 0) {
            fetchShiftStats();
            const refreshInterval = setInterval(fetchShiftStats, REFRESH_INTERVAL_MS);
            return () => clearInterval(refreshInterval);
        } else if (areaIds?.length === 0) {
            setShiftData([]); // Clear if no areas selected
            setUniqueTotals(null);
            setCountryTotals(null);
        }
    }, [isActive, areaIds, fetchShiftStats]);

    useEffect(() => {
        if (!isAdmin) {
            setShowCountrySplit(false);
        }
    }, [isAdmin]);

    // Handle Full Screen Toggle
    const toggleFullScreen = () => {
        if (!document.fullscreenElement) {
            containerRef.current?.requestFullscreen().catch(err => {
                console.error(`Error attempting to enable full-screen mode: ${err.message}`);
            });
        } else {
            document.exitFullscreen();
        }
    };

    useEffect(() => {
        const handleFullScreenChange = () => {
            const isNowFullScreen = !!document.fullscreenElement;
            setIsFullScreen(isNowFullScreen);
            if (!isNowFullScreen) {
                setShowCursor(true);
            }
        };
        document.addEventListener('fullscreenchange', handleFullScreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
    }, []);

    // Hide cursor after inactivity in fullscreen mode
    useEffect(() => {
        if (!isFullScreen) {
            setShowCursor(true);
            return;
        }

        const handleMouseMove = () => {
            setShowCursor(true);
            if (cursorTimeoutRef.current) {
                clearTimeout(cursorTimeoutRef.current);
            }
            cursorTimeoutRef.current = setTimeout(() => {
                setShowCursor(false);
            }, 3000);
        };

        // Start the timeout immediately
        cursorTimeoutRef.current = setTimeout(() => {
            setShowCursor(false);
        }, 3000);

        document.addEventListener('mousemove', handleMouseMove);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            if (cursorTimeoutRef.current) {
                clearTimeout(cursorTimeoutRef.current);
            }
        };
    }, [isFullScreen]);

    // Auto-scroll in fullscreen mode
    useEffect(() => {
        if (!isFullScreen || !scrollContainerRef.current) {
            return;
        }

        const scrollContainer = scrollContainerRef.current;
        let animationFrameId: number;

        const scroll = () => {
            if (autoScrollRef.current.paused) {
                animationFrameId = requestAnimationFrame(scroll);
                return;
            }

            const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
            const maxScroll = scrollHeight - clientHeight;

            if (autoScrollRef.current.direction === 'down') {
                if (scrollTop >= maxScroll - 1) {
                    // Reached bottom, pause then scroll up
                    autoScrollRef.current.paused = true;
                    autoScrollRef.current.pauseTimeout = setTimeout(() => {
                        autoScrollRef.current.direction = 'up';
                        autoScrollRef.current.paused = false;
                    }, AUTO_SCROLL_PAUSE_MS);
                } else {
                    scrollContainer.scrollTop += AUTO_SCROLL_SPEED;
                }
            } else {
                if (scrollTop <= 1) {
                    // Reached top, pause then scroll down
                    autoScrollRef.current.paused = true;
                    autoScrollRef.current.pauseTimeout = setTimeout(() => {
                        autoScrollRef.current.direction = 'down';
                        autoScrollRef.current.paused = false;
                    }, AUTO_SCROLL_PAUSE_MS);
                } else {
                    scrollContainer.scrollTop -= AUTO_SCROLL_SPEED;
                }
            }

            animationFrameId = requestAnimationFrame(scroll);
        };

        // Start auto-scroll after a brief delay
        const startTimeout = setTimeout(() => {
            animationFrameId = requestAnimationFrame(scroll);
        }, AUTO_SCROLL_PAUSE_MS);

        return () => {
            clearTimeout(startTimeout);
            cancelAnimationFrame(animationFrameId);
            if (autoScrollRef.current.pauseTimeout) {
                clearTimeout(autoScrollRef.current.pauseTimeout);
            }
            // Reset scroll state
            autoScrollRef.current = { direction: 'down', paused: false, pauseTimeout: null };
        };
    }, [isFullScreen]);

    // Calculate grid columns based on view mode
    const gridClasses = isFullScreen
        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4'
        : 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4';

    return (
        <div
            ref={containerRef}
            className={`flex-1 flex flex-col h-full bg-white dark:bg-gray-900 transition-all duration-300 ${isFullScreen ? 'p-6' : 'pb-8 pt-4'}`}
            style={isFullScreen && !showCursor ? { cursor: 'none' } : undefined}
        >
            {/* Hide scrollbar styles for fullscreen mode */}
            {isFullScreen && (
                <style>{`
                    .scrollbar-hide::-webkit-scrollbar {
                        display: none;
                    }
                `}</style>
            )}
            <div className={`container mx-auto px-4 ${isFullScreen ? 'max-w-full h-full' : 'max-w-7xl'} flex-1 flex flex-col`}>

                {/* Header */}
                <div className="flex justify-between items-start mb-6 flex-shrink-0 relative">
                    <div className="flex-1">
                        <h2 className={`${isFullScreen ? 'text-4xl' : 'text-3xl'} font-bold text-gray-900 dark:text-gray-100 tracking-tight transition-all`}>
                            Check-in Status
                        </h2>
                        {!isFullScreen && (
                            <p className="text-gray-500 dark:text-gray-400 text-lg mt-1">
                                Live check-in statistics by area
                            </p>
                        )}
                    </div>

                    {isFullScreen && <LogoIcon onClick={() => {}} className="absolute -top-3 left-1/2 -translate-x-1/2 !w-16 !h-16" /> }

                    <button
                        onClick={toggleFullScreen}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        title={isFullScreen ? "Exit Full Screen" : "Enter Full Screen"}
                    >
                        {isFullScreen ? <ArrowsContractIcon className="w-6 h-6" /> : <ArrowsExpandIcon className="w-6 h-6" />}
                    </button>
                </div>

                {loading && areaStats.length === 0 && (
                    <div className="flex justify-center items-center py-24 flex-1">
                        <LoadingSpinner meta={LoadingSpinnerMeta} />
                    </div>
                )}

                {error && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm text-center mb-6 flex-shrink-0">
                        {error}
                    </div>
                )}

                {!loading && !error && areaStats.length > 0 ? (
                    <>
                        {/* Total Summary Card - Sticky */}
                        <div className={`mb-6 p-5 bg-gradient-to-r from-primary-50 to-primary-100 dark:from-primary-900/30 dark:to-primary-800/30 rounded-xl border border-primary-200 dark:border-primary-700 flex-shrink-0 ${isFullScreen ? 'sticky top-0 z-10 shadow-lg' : ''}`}>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <h3 className="text-lg font-bold text-primary-900 dark:text-primary-100">
                                        Total Check-in Status
                                    </h3>
                                    <p className="text-sm text-primary-700 dark:text-primary-300 mt-0.5">
                                        {totalStats.area_count} area{totalStats.area_count !== 1 ? 's' : ''} · {totalStats.shift_count} shift{totalStats.shift_count !== 1 ? 's' : ''}
                                        {countUniqueVolunteers && ' · Unique volunteers'}
                                    </p>
                                </div>
                                <div className="flex items-center gap-6">
                                    <div className="text-center">
                                        <div className="text-3xl font-bold text-primary-900 dark:text-primary-100">
                                            {countUniqueVolunteers && uniqueTotals
                                                ? uniqueTotals.unique_checked_in_count
                                                : totalStats.checked_in_count}
                                            <span className="text-lg font-normal text-primary-600 dark:text-primary-400">
                                                {' / '}
                                                {countUniqueVolunteers && uniqueTotals
                                                    ? uniqueTotals.unique_accepted_count
                                                    : totalStats.accepted_count}
                                            </span>
                                        </div>
                                        <div className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                                            {countUniqueVolunteers ? 'Unique Volunteers' : 'Checked In'}
                                        </div>
                                    </div>
                                    <div className="text-center">
                                        {(() => {
                                            const pct = countUniqueVolunteers && uniqueTotals
                                                ? uniqueTotals.unique_checkin_percentage
                                                : totalStats.checkin_percentage;
                                            return (
                                                <div className={`text-3xl font-bold ${getCheckinTextColor(pct)}`}>
                                                    {pct}%
                                                </div>
                                            );
                                        })()}
                                        <div className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">Complete</div>
                                    </div>
                                </div>
                            </div>

                            {/* Options Checkboxes - Hidden in fullscreen */}
                            {!isFullScreen && (
                                <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-2">
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={countUniqueVolunteers}
                                            onChange={(e) => setCountUniqueVolunteers(e.target.checked)}
                                            className="h-4 w-4 text-primary-600 border-primary-300 rounded focus:ring-primary-500"
                                        />
                                        <span className="ms-2 text-sm text-primary-700 dark:text-primary-300">
                                            Count unique volunteers (total only)
                                        </span>
                                    </label>
                                    <label className="flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={filterByTime}
                                            onChange={(e) => setFilterByTime(e.target.checked)}
                                            className="h-4 w-4 text-primary-600 border-primary-300 rounded focus:ring-primary-500"
                                        />
                                        <span className="ms-2 text-sm text-primary-700 dark:text-primary-300">
                                            Current shifts only
                                        </span>
                                    </label>
                                    {isAdmin && (
                                        <label className="flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={showCountrySplit}
                                                onChange={(e) => setShowCountrySplit(e.target.checked)}
                                                className="h-4 w-4 text-primary-600 border-primary-300 rounded focus:ring-primary-500"
                                            />
                                            <span className="ms-2 text-sm text-primary-700 dark:text-primary-300">
                                                Split checked-in by EU vs UK/I
                                            </span>
                                        </label>
                                    )}
                                </div>
                            )}
                            {isAdmin && showCountrySplit && countryTotals && (
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {[
                                        { label: 'EU', stats: countryTotals.eu },
                                        { label: 'UK/I', stats: countryTotals.uk_ireland },
                                    ].map(({ label, stats }) => {
                                        const accepted = countUniqueVolunteers ? stats.unique_accepted_count : stats.accepted_count;
                                        const checkedIn = countUniqueVolunteers ? stats.unique_checked_in_count : stats.checked_in_count;
                                        const percentage = accepted > 0 ? Math.round((checkedIn / accepted) * 100) : 0;

                                        return (
                                            <div key={label} className="rounded-lg border border-primary-200 dark:border-primary-700 bg-white/70 dark:bg-gray-900/40 px-4 py-3">
                                                <div className="flex items-center justify-between gap-3">
                                                    <span className="text-sm font-semibold text-primary-800 dark:text-primary-200">{label}</span>
                                                    <span className="text-lg font-bold text-primary-900 dark:text-primary-100">
                                                        {checkedIn}
                                                        <span className="text-sm font-normal text-primary-600 dark:text-primary-400"> / {accepted}</span>
                                                    </span>
                                                </div>
                                                <div className="mt-2 flex items-center gap-3">
                                                    <div className="h-2 flex-1 rounded-full bg-primary-200 dark:bg-primary-700 overflow-hidden">
                                                        <div className={`h-full rounded-full transition-all duration-500 ${getCheckinBarColor(percentage)}`} style={{ width: `${percentage}%` }} />
                                                    </div>
                                                    <span className={`text-xs font-medium w-10 text-right ${getCheckinTextColor(percentage)}`}>{percentage}%</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {(() => {
                                const pct = countUniqueVolunteers && uniqueTotals
                                    ? uniqueTotals.unique_checkin_percentage
                                    : totalStats.checkin_percentage;
                                return (
                                    <div className="mt-4">
                                        <div className="w-full bg-primary-200 dark:bg-primary-700 rounded-full h-3 overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all duration-500 ${getCheckinBarColor(pct)}`}
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>

                        {/* Scrollable Area Cards Container */}
                        <div
                            ref={scrollContainerRef}
                            className={`${isFullScreen ? 'flex-1 overflow-y-auto scrollbar-hide' : ''}`}
                            style={isFullScreen ? { scrollbarWidth: 'none', msOverflowStyle: 'none' } : undefined}
                        >
                            {/* Area Cards Grid */}
                            <div className={`grid ${gridClasses} pb-10`}>
                            {areaStats.map((area) => {
                            const uniqueArea = areaUniqueTotals[area.area_id];
                            const accepted   = uniqueArea?.unique_accepted_count   ?? area.accepted_count;
                            const checkedIn  = uniqueArea?.unique_checked_in_count ?? area.checked_in_count;
                            const percentage = uniqueArea?.unique_checkin_percentage ?? area.checkin_percentage;

                            // Status Colors
                            const statusColor = getCheckinBorderColor(percentage);
                            const barColor = getCheckinBarColor(percentage);

                            return (
                                <div
                                    key={area.area_id}
                                    className={`
                                        group flex flex-col bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700
                                        ${statusColor} border-s-[4px] hover:shadow-md transition-shadow duration-200 animate-fadeIn
                                    `}
                                >
                                    <div className="p-3 flex flex-col h-full justify-between gap-2">

                                        {/* Top: Area Name */}
                                        <div className="flex items-center gap-2">
                                            <LocationIcon className="w-4 h-4 text-primary-500 flex-shrink-0" />
                                            <h4 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-1" title={area.area_name}>
                                                {area.area_name}
                                            </h4>
                                        </div>

                                        {/* Count Display */}
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="text-xl font-bold text-gray-900 dark:text-white">
                                                    {checkedIn}
                                                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400"> / {accepted}</span>
                                                </div>
                                            </div>
                                            <div className={`text-xl font-bold ${
                                                getCheckinTextColor(percentage)
                                            }`}>
                                                {percentage}%
                                            </div>
                                        </div>

                                        {/* Progress Bar */}
                                        <div className="w-full bg-gray-100 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all duration-500 ${barColor}`}
                                                style={{ width: `${percentage}%` }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                            })}
                            </div>
                        </div>
                    </>
                ) : !loading && !error && (
                    <div className="flex flex-col items-center justify-center py-24 px-4 flex-1 text-center">
                        <LocationIcon className="w-20 h-20 text-gray-300 mb-6" />
                        <h3 className="text-2xl font-bold text-gray-900 dark:text-white">No Areas Found</h3>
                        <p className="text-gray-500 dark:text-gray-400 text-lg mt-2 max-w-md">
                            {areaIds && areaIds.length > 0
                                ? "There are no active shifts for the selected areas at this time."
                                : "Please select one or more areas from the filter above to view live statistics."}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}